/* eslint-disable @typescript-eslint/no-inferrable-types */
import { apiPspService } from '@api/services/psp.apiService';
import { getEmptyPspInfo } from '@components-member/psp/getEmptyPspInfo';
import type { IPSPInfo } from '@components-member/psp/pspInfo.interface';
import type { IEnumConstant } from '@components-shared/enum.constants.interface';
import { GENERAL_CONSTANTS } from '@components-shared/general.constants';
import { IAccount, IAccountApiResponse } from '@components-shared/shared.interfaces';
import { AxiosError } from 'axios';
import { PSP_INFO_LOADING_STATUS } from 'global.enums';
import type { IAccountSettings, IPlugin, IWallet, InternalTransferInfo } from 'global.interfaces';
import { makeAutoObservable, runInAction } from 'mobx';
import type { MobxRootStore } from './mobx.store';

export class PSPStore {
  mobxStore: MobxRootStore;

  private _plugins: IPlugin[] = [];
  private _pluginLoading: boolean = true;
  private _selectedCategory: string | null = null;
  private _selectedPlugin: IPlugin | null = null;
  private _infoLoadingState: PSP_INFO_LOADING_STATUS = PSP_INFO_LOADING_STATUS.EMPTY;
  private _pluginInfo: IPSPInfo = getEmptyPspInfo();
  private _tradingAccounts: IAccountApiResponse[] = [];
  private _walletAccounts: IWallet[] = [];
  private _accountsLoading: boolean = true;
  private _internalTransferInfo: InternalTransferInfo | Record<string, never> = {};
  private _internalTransferInfoLoading: boolean = true;
  private _wallets: IWallet[] = [];
  private _walletsLoading: boolean = true;

  constructor(mobxStore: MobxRootStore) {
    this.mobxStore = mobxStore;
    makeAutoObservable(this);
  }

  fetchPspPlugins = async (pspType: string) => {
    this._plugins = [];
    this._pluginLoading = true;
    this._selectedCategory = null;

    try {
      const response = await apiPspService.getPspPlugins(pspType);

      runInAction(() => {
        // @ts-ignore
        this._plugins = response.data?.data || response.data;
        this._pluginLoading = false;
      });

      const getCategories = (currentPlugins: IPlugin[] = []) => {
        const categories = GENERAL_CONSTANTS.WALLET_OPERATION_CATEGORY as IEnumConstant[];
        const pluginsCategories = currentPlugins.map((pl) => pl.category);

        return categories.filter((cat) => pluginsCategories.indexOf(cat.value) !== -1);
      };

      const categories = getCategories(response.data as IPlugin[]);

      this.setPspCategory(categories[0] ? categories[0].value || null : null);
    } catch (error) {
      runInAction(() => {
        this._plugins = [];
        this._pluginLoading = false;

        /**
         * @description Legacy code migrated from redux
         * @todo: Verify if we need it
         */
        const payload = (error as { data?: unknown })?.data ?? (error as AxiosError).response?.data;

        if (payload?.plugins) {
          this._plugins = payload.plugins;
        }
      });
    }
  };

  fetchPspPluginInfo = async (type: string, pspId: string | number) => {
    this._pluginInfo = getEmptyPspInfo();
    this._infoLoadingState = PSP_INFO_LOADING_STATUS.LOADING;
    try {
      const response = await apiPspService.getPspPluginInfo({ type, pspId });

      runInAction(() => {
        this._pluginInfo = { ...response.data };
        this._infoLoadingState = PSP_INFO_LOADING_STATUS.LOADED_SUCCESS;
      });
    } catch (error) {
      runInAction(() => {
        const payload = error?.data ?? (error as AxiosError).response?.data;

        this._pluginInfo = { ...payload };

        this._infoLoadingState = PSP_INFO_LOADING_STATUS.LOADED_FAIL;
      });
    }
  };

  setPspCategory = (category: string | null) => {
    this._selectedCategory = category;
  };

  setPspPlugin = (plugin: IPlugin | null) => {
    this._selectedPlugin = plugin;
  };

  setPspInfoEmpty = () => {
    this._pluginInfo = getEmptyPspInfo();
    this._infoLoadingState = PSP_INFO_LOADING_STATUS.EMPTY;
  };

  fetchAllAccounts = async (accountType: string) => {
    this._accountsLoading = true;

    try {
      const [accounts, walletAccounts] = await Promise.all([
        apiPspService.getAccounts(accountType),
        apiPspService.getWalletsAccount(),
      ]);

      runInAction(() => {
        this._accountsLoading = false;
        this._tradingAccounts = accounts.data;
        this._walletAccounts = walletAccounts.data;
      });
    } catch (error) {
      runInAction(() => {
        this._accountsLoading = false;
      });
    }
  };

  fetchInternalTransferInfo = async () => {
    this._internalTransferInfoLoading = true;
    this._internalTransferInfo = {};

    try {
      const response = await apiPspService.getInternalTransferInfo();

      runInAction(() => {
        this._internalTransferInfo = response.data;
        this._internalTransferInfoLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this._internalTransferInfoLoading = false;
      });
    }
  };

  fetchWalletAccounts = async () => {
    this._walletsLoading = true;

    try {
      const response = await apiPspService.getWalletsAccount();

      runInAction(() => {
        this._wallets = response.data;
        this._walletsLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this._walletsLoading = false;
      });
    }
  };

  get categories(): IEnumConstant[] {
    const categories = GENERAL_CONSTANTS.WALLET_OPERATION_CATEGORY as IEnumConstant[];
    const plugins = this._plugins ?? [];

    const pluginsCategories = plugins.map((pl) => pl.category);

    return categories.filter((cat) => pluginsCategories.indexOf(cat.value) !== -1);
  }

  get allAccountOptions(): {
    settings?: IAccountSettings;
    balance: number;
    walletType: string | null;
    accountType: string;
    currency: string;
    id: number;
    title?: string;
    value: string;
    idHash?: IAccount['idHash'];
  }[] {
    const accounts = this._tradingAccounts ?? [];
    const walletAccounts = this._walletAccounts ?? [];

    const options = [...walletAccounts, ...accounts.filter((acc) => !!acc.currency)];

    return options.map((option) => {
      const accountType = 'walletNumber' in option ? 'wallet' : 'account';
      let title;

      if ('walletNumber' in option) {
        title = option.walletNumber;
      } else if ('login' in option) {
        title = `${option.login}`;
      }

      return Object.assign(
        {},
        {
          ...option,
          title,
          value: `${accountType}-${option.id}`,
          walletType: 'type' in option ? option.type : null,
          accountType,
        },
      );
    });
  }

  get plugins() {
    return this._plugins;
  }
  get pluginLoading() {
    return this._pluginLoading;
  }
  get selectedCategory() {
    return this._selectedCategory;
  }
  get selectedPlugin() {
    return this._selectedPlugin;
  }
  get infoLoadingState() {
    return this._infoLoadingState;
  }
  get pluginInfo() {
    return this._pluginInfo;
  }
  get tradingAccounts() {
    return this._tradingAccounts;
  }
  get walletAccounts() {
    return this._walletAccounts;
  }
  get accountsLoading() {
    return this._accountsLoading;
  }
  get internalTransferInfo() {
    return this._internalTransferInfo;
  }
  get internalTransferInfoLoading() {
    return this._internalTransferInfoLoading;
  }
  get wallets() {
    return this._wallets;
  }
  get walletsLoading() {
    return this._walletsLoading;
  }
}
